import type { LibraryKey } from '../types'
import { API_BASE, authHeaders } from './apiAuth'

/**
 * A search sentence, understood by the Worker (smartSearch.ts, 2026-11-02):
 * per library, the tags it means, and a few English words for the rest.
 */
export interface UnderstoodSearch {
  /** Each tag with its weight: what the search is mainly after (core) counts far more. */
  libraries: Partial<
    Record<LibraryKey, { id: string; name: string; group: string; weight?: number; core?: boolean; alias?: string }[]>
  >
  words: string[]
  /** Each word's weight, in the order of `words`. */
  wordWeights?: number[]
  coreWords?: string[]
  /** The whole search in English, matched against picture descriptions as a whole (2026-11-03). */
  phrase?: string
  cached: boolean
}

/**
 * Whether a search is sent to be understood: any Chinese, Japanese or Korean
 * in it, or two words or more (Huanrui, 2026-11-02: "chocolate milk" was
 * searched as two words that must both be found, and found one picture).
 * A single word keeps the ordinary, instant search.
 */
export function isSentence(text: string): boolean {
  const words = text.trim()
  if (!words) return false
  if (/[぀-ヿ㐀-鿿가-힯]/.test(words)) return true
  return words.split(/\s+/).length >= 2
}

export async function understandSearch(text: string, libraries: LibraryKey[]): Promise<UnderstoodSearch> {
  const response = await fetch(`${API_BASE}/api/ai/understand-search`, {
    method: 'POST',
    signal: AbortSignal.timeout(25_000),
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ text, libraries }),
  })
  if (!response.ok) throw new Error(`The search could not be understood (HTTP ${response.status})`)
  return (await response.json()) as UnderstoodSearch
}

/**
 * Amazon or Google pictures, also into the Internal Library (Audit), when the
 * box beside Send was ticked (Worker: externalToLibrary.ts, 2026-11-02).
 *
 * Handed over and not waited for: the Worker queues it and fetches the
 * pictures itself, so sending to Miro or a project is no slower. `keepalive`
 * so the request still goes when the page or Miro's window closes straight
 * after -- which is also why only the addresses are sent, never the pictures.
 */
export function addExternalToLibrary(
  source: 'amazon' | 'google',
  query: string,
  pictures: { url: string; title?: string; page?: string }[],
): void {
  const wanted = pictures
    .filter((one) => /^https?:\/\//i.test(one.url))
    .slice(0, 40)
    .map((one) => ({ url: one.url, title: (one.title ?? '').slice(0, 200), page: (one.page ?? '').slice(0, 500) }))
  if (wanted.length === 0) return
  void authHeaders()
    .then((headers) =>
      fetch(`${API_BASE}/api/ai/external-to-library`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ source, query: query.trim().slice(0, 200), pictures: wanted }),
      }),
    )
    .then((response) => {
      if (!response.ok) console.warn('the pictures were not added to the Internal Library', response.status)
    })
    .catch((error: unknown) => console.warn('the pictures were not added to the Internal Library', error))
}

/* ---------- a sentence understood, for any search box (2026-11-02) ---------- */

/** What a sentence was understood as: per library, its tags, and some words. */
export type SearchPlan = UnderstoodSearch

export function planIsEmpty(plan: SearchPlan | null | undefined): boolean {
  return (
    !plan ||
    (plan.words.length === 0 &&
      !plan.phrase &&
      Object.values(plan.libraries).every((tags) => !tags || tags.length === 0))
  )
}

/**
 * The plan for a search: a sentence is understood (a second or two, once per
 * sentence -- the Worker keeps the answer); a word or two, or anything that
 * could not be understood, gives null and the ordinary search.
 */
export async function planFor(text: string, libraries: LibraryKey[]): Promise<SearchPlan | null> {
  if (!isSentence(text)) return null
  try {
    const plan = await understandSearch(text.trim(), libraries)
    return planIsEmpty(plan) ? null : plan
  } catch (error) {
    console.warn('the search could not be understood; searching the words', error)
    return null
  }
}

/**
 * What one library is searched with under a plan: its tags and the words.
 * Null when the plan has nothing for that library -- it has nothing to score
 * by, and is left out rather than searched for everything.
 */
export function smartFor(
  plan: SearchPlan,
  library: LibraryKey,
): {
  tagIds: string[]
  tagWeights: number[]
  tagAliases: string[]
  words: string[]
  wordWeights: number[]
  coreMin: number
  phrase: string
} | null {
  const tags = plan.libraries[library] ?? []
  // The phrase alone is enough: a library with no tag or word for the search
  // can still have pictures whose description says it all.
  if (tags.length === 0 && plan.words.length === 0 && !plan.phrase) return null
  return {
    tagIds: tags.map((tag) => tag.id),
    tagWeights: tags.map((tag) => tag.weight ?? 3),
    tagAliases: tags.map((tag) => tag.alias ?? ''),
    phrase: plan.phrase ?? '',
    words: plan.words,
    wordWeights: plan.words.map((_, i) => plan.wordWeights?.[i] ?? 2),
    // What only a picture matching the core can score (a core tag 1000 and
    // up, a core word 560 and up): when some picture does, only those are
    // shown (docs/smart-search.sql).
    coreMin: tags.some((tag) => tag.core) || (plan.coreWords?.length ?? 0) > 0 ? CORE_MIN : 0,
  }
}

/** The score that only a picture matching what the search is mainly after reaches. */
const CORE_MIN = 500

/** The tags of a plan, each name once whichever libraries it is in. */
export function planTags(plan: SearchPlan | null): { name: string; group: string; core?: boolean }[] {
  if (!plan) return []
  return [...new Map(Object.values(plan.libraries).flat().map((tag) => [tag!.name, tag!])).values()]
}

/** A plan with one tag (by name, in every library) or one word taken off; null when nothing is left. */
export function dropFromPlan(plan: SearchPlan, piece: { tag?: string; word?: string }): SearchPlan | null {
  const kept = plan.words.map((word, i) => ({ word, weight: plan.wordWeights?.[i] ?? 2 })).filter((one) => one.word !== piece.word)
  const next: SearchPlan = {
    ...plan,
    words: kept.map((one) => one.word),
    wordWeights: kept.map((one) => one.weight),
    libraries: Object.fromEntries(
      Object.entries(plan.libraries).map(([library, tags]) => [library, (tags ?? []).filter((tag) => tag.name !== piece.tag)]),
    ) as SearchPlan['libraries'],
  }
  return planIsEmpty(next) ? null : next
}
